import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { db } from "../firebase";
import { collection, query, where, getDocs } from "firebase/firestore";
import "./BlogDetailPage.css";
import Footer from "../Footer";

// Content Agent posts are written as Markdown (see biba-erp's
// content_agent.py) so an inline illustration can appear as
// ![alt](url) alongside the hero image; older posts written before this
// were plain text, which Markdown renders through unchanged (a lone
// "#"/"*" is rare in prose and, worst case, just renders literally).
const blogMarkdownComponents = {
  p: ({ node, ...props }) => <p className="blog-paragraph" {...props} />,
  // eslint-disable-next-line jsx-a11y/heading-has-content -- children arrive via {...props} at render time
  h1: ({ node, ...props }) => <h2 className="blog-subheading" {...props} />,
  // eslint-disable-next-line jsx-a11y/heading-has-content
  h2: ({ node, ...props }) => <h2 className="blog-subheading" {...props} />,
  // eslint-disable-next-line jsx-a11y/heading-has-content
  h3: ({ node, ...props }) => <h3 className="blog-subheading" {...props} />,
  ul: ({ node, ...props }) => <ul className="blog-list" {...props} />,
  ol: ({ node, ...props }) => <ol className="blog-list" {...props} />,
  img: ({ node, ...props }) => (
    // eslint-disable-next-line jsx-a11y/alt-text
    <img className="blog-inline-image" loading="lazy" {...props} />
  ),
  // GFM tables (spec comparisons); the wrapper scrolls sideways on phones
  // instead of squeezing the columns.
  table: ({ node, ...props }) => (
    <div className="blog-table-wrap">
      <table className="blog-table" {...props} />
    </div>
  ),
  // Used for formulas / key definitions.
  blockquote: ({ node, ...props }) => <blockquote className="blog-callout" {...props} />,
};


// Helper: Convert title to URL slug
const slugify = (text) => {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_]+/g, "-")
    .replace(/^-+|-+$/g, "");
};


const BlogDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // ✅ Fetch blog post by slug
        const q = query(
          collection(db, "blogs"),
          where("status", "==", "published")
        );

        const querySnapshot = await getDocs(q);
        const blogs = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));

        // A post may carry an optional short `slug` (set in Firestore) for
        // links that have to fit in print, e.g. the datasheet reference
        // table. The title-derived URL keeps working and forwards to the
        // short one, so each post has a single canonical address.
        const foundBlog =
          blogs.find(blog => blog.slug && blog.slug === slug) ||
          blogs.find(blog => slugify(blog.title) === slug);

        if (!foundBlog) {
          setError("Blog post not found");
          setLoading(false);
          return;
        }

        if (foundBlog.slug && foundBlog.slug !== slug) {
          navigate(`/blog/${foundBlog.slug}`, { replace: true });
          return;
        }

        setPost(foundBlog);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [slug, navigate]);

  const formatDate = (timestamp) => {
    if (!timestamp) return "Date unavailable";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getReadTime = (content) => {
    if (!content) return "5 min read";
    const wordCount = content.split(/\s+/).length;
    const minutes = Math.max(1, Math.ceil(wordCount / 200));
    return `${minutes} min read`;
  };

  // Loading state
  if (loading) {
    return (
      <div className="blog-detail-container">
        {/* No noindex here: the static page already says "index", and a crawler that
            renders JavaScript must not see "noindex" for the moment the post is loading. */}
        <div className="blog-loading">
          <p>Loading blog post...</p>
        </div>
        <Footer />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="blog-detail-container">
        <Helmet>
          <meta name="robots" content="noindex, follow" />
        </Helmet>
        <div className="blog-error">
          <button className="blog-back-btn" onClick={() => navigate("/about")}>
            ← Back to Blog
          </button>
          <h2>⚠️ Blog Post Not Found</h2>
          <p>{error}</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="blog-detail-container">
        <Helmet>
          <meta name="robots" content="noindex, follow" />
        </Helmet>
        <div className="blog-error">
          <button className="blog-back-btn" onClick={() => navigate("/about")}>
            ← Back to Blog
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const metaTitle = `${post.title} | BIBA Electronics Blog`;
  // Plain text: strip the Markdown syntax so the description reads as prose
  // (same rule as scripts/blog-prerender.mjs, which writes the static copy).
  const plainText = (md) =>
    String(md || "")
      .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
      .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
      .replace(/^#{1,6}\s+/gm, "")
      .replace(/[*_`>|]/g, "")
      .replace(/\s+/g, " ")
      .trim();
  const metaDescription = plainText(post.excerpt || post.content || post.title).slice(0, 160);

  return (
    <div className="blog-detail-container">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        {post.image && <meta property="og:image" content={post.image} />}
        <meta property="og:type" content="article" />
      </Helmet>
      {/* ✅ HERO SECTION WITH FEATURED IMAGE */}
      {post.image && (
        <div className="blog-hero-section">
          <img
            src={post.image}
            alt={post.title}
            className="blog-hero-image"
          />
          <div className="blog-hero-overlay"></div>
        </div>
      )}

      {/* ✅ MAIN CONTENT AREA */}
      <article className="blog-article-wrapper">
        {/* Header with title and metadata */}
        <header className="blog-header">
          <div className="blog-header-inner">
            {/* Breadcrumb */}
            <nav className="blog-breadcrumb">
              <a onClick={() => navigate("/")} className="breadcrumb-link">Home</a>
              <span className="breadcrumb-sep">/</span>
              <a onClick={() => navigate("/about")} className="breadcrumb-link">Blog</a>
              <span className="breadcrumb-sep">/</span>
              <span className="breadcrumb-current">{post.title}</span>
            </nav>

            {/* Title */}
            <h1 className="blog-title">{post.title}</h1>

            {/* Metadata */}
            <div className="blog-meta">
              <span className="blog-category">{post.category}</span>
              <span className="blog-meta-divider">•</span>
              <span className="blog-date">📅 {formatDate(post.createdAt)}</span>
              <span className="blog-meta-divider">•</span>
              <span className="blog-read-time">⏱️ {getReadTime(post.content)}</span>
              {post.author && (
                <>
                  <span className="blog-meta-divider">•</span>
                  <span className="blog-author">By {post.author}</span>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Main article content */}
        <div className="blog-content-wrapper">
          <div className="blog-content">
            {/* Content paragraphs */}
            <div className="blog-body">
              {post.content && (
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={blogMarkdownComponents}>
                  {post.content}
                </ReactMarkdown>
              )}
            </div>

            {/* Author bio section */}
            {post.author && (
              <div className="blog-author-section">
                <div className="author-content">
                  <h3 className="author-name">{post.author}</h3>
                  <p className="author-bio">Author at BIBA Electronics</p>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="blog-navigation">
              <button
                className="blog-nav-btn"
                onClick={() => navigate("/about")}
              >
                ← Back to Blog
              </button>
            </div>
          </div>
        </div>
      </article>

    </div>
  );
};

export default BlogDetailPage;